import { BaseSyntheticEvent, useCallback, useState } from "react";
import { Loader } from "../../components/ui/Loader";
import { DataTable } from "primereact/datatable";
import { TableHeader } from "../../components/ui/table-header";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { useToast } from "../../components/ui/toast-context-provider";
import { useQueryClient } from "react-query";
import {
  CustomModal,
  CustomModalProps,
} from "../../components/ui/MobileModal/custom-modal";
import { FilterMatchMode } from "primereact/api";
import { RideTypeForm } from "./RideTypesForm";
import {
  useAddRideTypeMutation,
  useDeleteRideTypeMutation,
  useEditRideTypeMutation,
  useRideTypesQuery,
} from "../../queries/ride-types.query";
import { RideType } from "../../queries/models/ride-type.model";

export function RideTypesPage() {
  const queryClient = useQueryClient();
  const RideTypesQuery = useRideTypesQuery();
  const addRideTypeMutation = useAddRideTypeMutation();
  const editRideTypeMutation = useEditRideTypeMutation();
  const deleteRideTypeMutation = useDeleteRideTypeMutation();
  const toast = useToast();
  const [selectedRideType, setSelectedRideType] = useState<RideType>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const handleGlobalFilterChange = useCallback(
    (e: BaseSyntheticEvent) => {
      const value = e.target.value;
      let _filters = filters;
      _filters.global.value = value;

      setFilters(_filters);
      setGlobalFilterValue(value);
    },
    [filters]
  );

  const handleAddNewRideType = () => {
    setSelectedRideType({
      name: "",
    } as RideType);
  };

  const handleSaveRideType = (data: RideType) => {
    return data?.id ? editExistingRideType(data) : saveNewRideType(data);
  };

  const saveNewRideType = (data: RideType) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedRideType(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: RideType = {
      id: data.id,
      name: data.name,
      order: data.order,
      comment: data.comment,
    };

    return addRideTypeMutation.mutateAsync(request, mutateOptions);
  };

  const editExistingRideType = (data: RideType) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedRideType(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: RideType = {
      id: data.id,
      name: data.name,
      order: data.order,
      comment: data.comment,
    };

    return editRideTypeMutation.mutateAsync(request, mutateOptions);
  };

  const mobileRideTypeFormModalProps: CustomModalProps = {
    header: selectedRideType?.id ? selectedRideType.name : "Dodaj nowy typ",
    onClose: () => {
      setSelectedRideType(undefined);
      setIsModalOpen(false);
    },
    isOpen: isModalOpen,
    body: (
      <RideTypeForm
        rideType={selectedRideType ?? ({} as RideType)}
        onSave={handleSaveRideType}
        onCancel={() => {
          setSelectedRideType(undefined);
          setIsModalOpen(false);
        }}
        onDelete={() => setIsDeleteConfirmationModalOpen(true)}
        isSubmitting={isSubmitting}
      />
    ),
  };

  const handleDeleteRideType = useCallback(() => {
    if (selectedRideType) {
      deleteRideTypeMutation.mutateAsync(selectedRideType.id, {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          toast.current?.show({
            severity: "success",
            detail: "Zapisano!",
          });
          setSelectedRideType(undefined);
          setIsDeleteConfirmationModalOpen(false);
          setIsModalOpen(false);
        },
        onError: async (error: any) => {
          toast.current?.show({
            severity: "error",
            detail: error.message,
          });
        },
      });
    }
  }, [deleteRideTypeMutation, queryClient, selectedRideType, toast]);

  const mobileModalConfirmationFormProps: CustomModalProps = {
    header: "Potwierdź",
    body: "Czy chcesz usunąć?",
    height: "160px",
    width: "50%",
    isOpen: isDeleteConfirmationModalOpen,
    confirmation: true,
    onClose: () => setIsDeleteConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: handleDeleteRideType,
  };

  return (
    <>
      {RideTypesQuery.isLoading ? (
        <div className="h-full flex align-items-center">
          <Loader type="spinner" />
        </div>
      ) : (
        <div className="h-full">
          {!!mobileRideTypeFormModalProps && (
            <CustomModal {...mobileRideTypeFormModalProps} />
          )}
          {!!mobileModalConfirmationFormProps && (
            <CustomModal {...mobileModalConfirmationFormProps} />
          )}
          <div className="p-1 h-3rem">
            <InputText
              className="w-full"
              placeholder="Filtr globalny"
              value={globalFilterValue}
              onChange={handleGlobalFilterChange}
            />
          </div>

          <div style={{ height: "calc(100% - 3rem)" }}>
            <DataTable
              value={RideTypesQuery.data}
              resizableColumns={true}
              scrollable={true}
              scrollHeight="flex"
              selectionMode="single"
              selection={selectedRideType}
              onSelectionChange={(e) => {
                setSelectedRideType(e.value as RideType);
                setIsModalOpen(true);
              }}
              filters={filters}
              globalFilterFields={["name", "comment"]}
              header={
                <TableHeader
                  header="Rodzaje jazd"
                  showAddButton={true}
                  onClickAdd={() => {
                    handleAddNewRideType();
                    setIsModalOpen(true);
                  }}
                />
              }
            >
              <Column field="order" header="Kolejność" sortable />
              <Column field="name" header="Nazwa" sortable />
            </DataTable>
          </div>
        </div>
      )}
    </>
  );
}
